import { useEffect, useState } from "react";
import type { ProjectStory } from "@/mocks/gallery";

interface ProjectHeroProps {
  project: ProjectStory;
  scrollY: number;
}

const clamp01 = (x: number) => Math.max(0, Math.min(1, x));

/**
 * Full-bleed cinematic hero for the project story page.
 *
 * - Parallax zoom on the background image tied to scroll
 * - Dark overlay for text legibility
 * - Interactive mouse-follow warm light glow that traces the cursor
 *   across the image, so it feels like the viewer is holding a lantern
 */
export default function ProjectHero({ project, scrollY }: ProjectHeroProps) {
  const [mouse, setMouse] = useState({ x: 50, y: 50 });
  const [heroH, setHeroH] = useState(
    typeof window !== "undefined" ? window.innerHeight : 900
  );

  useEffect(() => {
    const onResize = () => setHeroH(window.innerHeight);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const heroProgress = clamp01(scrollY / heroH);
  const zoom = 1.08 + heroProgress * 0.06;
  const dimUp = 1 - heroProgress * 0.4;

  const handleMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMouse({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  };

  return (
    <section
      className="relative w-full h-screen overflow-hidden"
      onMouseMove={handleMove}
    >
      {/* Base image, parallax zoom */}
      <div
        className="absolute inset-0"
        style={{
          transform: `scale(${zoom}) translateY(${heroProgress * -2}%)`,
          transformOrigin: "center 55%",
          willChange: "transform",
        }}
      >
        <img
          src={project.heroImage}
          alt={`${project.title} — hero`}
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
      </div>

      {/* Warm mouse-follow lantern glow */}
      <div
        className="absolute inset-0 pointer-events-none transition-opacity duration-500"
        style={{
          opacity: dimUp * 0.65,
          background: `radial-gradient(circle at ${mouse.x}% ${mouse.y}%, rgba(255, 190, 130, 0.25) 0%, rgba(255, 160, 90, 0.10) 20%, rgba(0,0,0,0) 45%)`,
          mixBlendMode: "screen",
        }}
      />

      {/* Global dimming overlays for text legibility */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.75) 100%)",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/30 pointer-events-none" />

      {/* Bottom-left title block */}
      <div
        className="absolute bottom-16 md:bottom-24 left-6 md:left-14 right-6 md:right-14 z-10 pointer-events-none"
        style={{
          opacity: dimUp,
          transform: `translateY(${heroProgress * 24}px)`,
        }}
      >
        <div className="text-[10px] uppercase tracking-[0.4em] text-white/60 mb-6">
          {project.location} · {project.year}
        </div>
        <h1
          className="font-black uppercase leading-[0.88] tracking-[-0.03em] text-white max-w-5xl"
          style={{ fontSize: "clamp(48px, 8vw, 132px)" }}
        >
          {project.title}
        </h1>
        <p
          className="mt-8 max-w-2xl text-white/75 font-light leading-relaxed"
          style={{ fontSize: "clamp(15px, 1.15vw, 20px)" }}
        >
          {project.tagline}
        </p>
      </div>

    </section>
  );
}